import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './en.json';
import es from './es.json';

export const LOCALES = ['en', 'es'] as const;
export type Locale = (typeof LOCALES)[number];

const STORAGE_KEY = 'guabi.locale';

export const isLocale = (v: string | null | undefined): v is Locale =>
  LOCALES.includes(v as Locale);

/**
 * What language to open in.
 *
 * A stored choice wins, then the browser. Only the language part is read:
 * `es-CO`, `es-419` and `es` are all Spanish, and matching the whole tag would
 * leave a Colombian browser in English.
 */
export function detectLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // Private windows and blocked site data throw on read. Fall through.
  }
  const browser = (navigator.language ?? 'en').slice(0, 2).toLowerCase();
  return isLocale(browser) ? browser : 'en';
}

export function setLocale(locale: Locale): void {
  void i18n.changeLanguage(locale);
  document.documentElement.lang = locale;
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Not being able to remember the choice is not a reason to refuse it.
  }
}

void i18n.use(initReactI18next).init({
  resources: { en: { translation: en }, es: { translation: es } },
  lng: detectLocale(),
  // English is the fallback, but `es.json` is complete and a test keeps it that
  // way — a fallback that actually fires means a half-Spanish screen.
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

document.documentElement.lang = i18n.language;

export default i18n;
